import { defineStore } from "pinia"
import axios from "axios"
import type { AxiosResponse } from "@/plugins/axios"
import moment from "@/utils/moment"
import {Device} from "@capacitor/device"
import {Capacitor} from "@capacitor/core"
import {Preferences} from "@capacitor/preferences"
import { useUsersStore } from "@/stores/users"
import { identify as posthogIdentify, reset as posthogReset } from "@/plugins/posthog"

const PIN_TRUST_TOKEN_KEY = "pin_trust_token"

export interface OtpForm {
  phone: string
  register: boolean
}

/**
 * Form interface for login with password or OTP+password
 *
 * Mode 'login': phone + password (existing users with password)
 * Mode 'signup' or 'set_password': otp + password + password_confirmation
 * Mode 'reset_password': otp + password + password_confirmation (password reset)
 */
export interface LoginForm {
  phone?: string
  password: string
  password_confirmation?: string
  otp?: string
  mode: 'login' | 'signup' | 'set_password' | 'reset_password'
}

export interface PinLoginForm {
  user_id: number
  pin: string
  remember?: boolean
  device_id?: string
}


export const useUserStore = defineStore("user", {
  state: () => {
    return {
      user: null as App.Models.AuthUser | null
    }
  },
  actions: {
    sendOtp(form: OtpForm) {
      return axios.post("/auth/otp", form)
    },
    sendPasswordResetOtp(phone: string) {
      return axios.post("/auth/password-reset-otp", { phone })
    },

    async loginWithPassword(form: { phone: string; password: string }) {
      const response: AxiosResponse<{ access_token: string }> = await axios
        .post("/auth/login/password", form)
        .catch((e) => e)

      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token)
        await this.loadUser(true)
      }

      return response
    },

    async signupWithOtp(form: { otp: string; password: string; password_confirmation: string; phone: string }) {
      const response: AxiosResponse<{ access_token: string }> = await axios
        .post("/auth/signup", form)
        .catch((e) => e)

      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token)
        await this.loadUser(true)
      }

      return response
    },

    async loginWithOtp(form: { otp: string; password: string; password_confirmation: string; phone: string }) {
      const response: AxiosResponse<{ access_token: string }> = await axios
        .post("/auth/login/otp", form)
        .catch((e) => e)

      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token)
        await this.loadUser(true)
      }

      return response
    },

    async resetPassword(form: { otp: string; password: string; password_confirmation: string; phone: string }) {
      const response: AxiosResponse<{ access_token: string }> = await axios
        .post("/auth/password/reset", form)
        .catch((e) => e)

      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token)
        await this.loadUser(true)
      }

      return response
    },

    async pinLogin(form: PinLoginForm) {
      const response: AxiosResponse<{ access_token: string; pin_trust_token?: string }> = await axios
        .post("/auth/pin-login", form)
        .catch((e) => e)

      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token)
        await this.loadUser(true)
      }

      // Persist the server-signed trust token. Tampering with the stored value
      // fails signature verification on the backend, so localStorage is acceptable
      // here under the same threat model as the JWT.
      if (form.remember && response.data.pin_trust_token) {
        await this.savePinTrustToken(response.data.pin_trust_token)
      }

      return response
    },
    async verifyPinTrust() {
      const storedToken = await this.readPinTrustToken()
      if (!storedToken) {
        return { valid: false as const }
      }

      const deviceId = (await Device.getId().catch(() => null))?.identifier

      const response = await axios
        .post<{ valid: boolean; user_id?: number; access_token?: string }>("/auth/pin-trust/verify", {
          token: storedToken,
          device_id: deviceId,
        })
        .catch((e) => e)

      if (response?.data?.valid && response.data.access_token) {
        localStorage.setItem("token", response.data.access_token)
        await this.loadUser(true)
        return { valid: true as const, userId: response.data.user_id }
      }

      // Don't clear the stored token here. A `valid: false` may simply mean the auth'd user
      // is in a different account or the verify isn't applicable right now — the token can
      // still be valid for a future verify (e.g. after a profile switch back). Real clearing
      // happens on logout / forgot-PIN.
      return { valid: false as const }
    },
    async clearPinTrust() {
      localStorage.removeItem(PIN_TRUST_TOKEN_KEY)
      if (Capacitor.isNativePlatform()) {
        await Preferences.remove({ key: PIN_TRUST_TOKEN_KEY }).catch(() => { /* non-fatal */ })
      }
    },
    async savePinTrustToken(token: string) {
      if (Capacitor.isNativePlatform()) {
        await Preferences.set({ key: PIN_TRUST_TOKEN_KEY, value: token }).catch(() => { /* non-fatal */ })
      } else {
        localStorage.setItem(PIN_TRUST_TOKEN_KEY, token)
      }
    },
    async readPinTrustToken(): Promise<string | null> {
      if (Capacitor.isNativePlatform()) {
        const { value } = await Preferences.get({ key: PIN_TRUST_TOKEN_KEY }).catch(() => ({ value: null }))
        return value
      }
      return localStorage.getItem(PIN_TRUST_TOKEN_KEY)
    },
    async logout() {
      const deviceId = await Device.getId() || null
      await axios.delete("/auth", {
        data: {
          device_id: deviceId ? deviceId.identifier : undefined
        }
      }).catch((e) => e)
      this.user = null
      localStorage.removeItem('token')
      await this.clearPinTrust()
      useUsersStore().reset()
      posthogReset()
    },
    async loadUser(force: boolean = false) {
      if (this.user && !force) {
        return this.user
      }
      // A failed /auth lands here as an error shaped object, so pull the user out
      // defensively. Destructuring it blind used to store `undefined` and every
      // caller that trusted the store then crashed on it.
      const response: AxiosResponse<{ user: App.Models.AuthUser }> = await axios.get("/auth").catch((e) => e)
      const user = response?.data?.user ?? null
      this.user = user
      if (user) {
        posthogIdentify(user)
        moment.updateLocale('en', {
          week: {
            dow: user.settings.start_of_week
          }
        })
      }
      return user
    },
    async impersonate(userId: number) {
      const response: AxiosResponse<{ access_token: string }> = await axios
        .post("/auth/impersonate", {
          user_id: userId
        })
        .catch((e) => e)

      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token)
        await this.loadUser(true)
      }

      return response
    },
    async stopImpersonating() {
      const response: AxiosResponse<{ access_token: string }> = await axios
        .delete("/auth/stop-impersonating")
        .catch((e) => e)

      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token)
        await this.loadUser(true)
      }

      return response
    }
  }
})
